import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Plus, Pencil, Trash2, Languages, Newspaper, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TARGET_LANGS = ['en', 'pt', 'id', 'vi', 'es', 'tl', 'zh'];
const LANG_LABELS = { ja: '日本語', en: 'EN', pt: 'PT', id: 'ID', vi: 'VI', es: 'ES', tl: 'TL', zh: '中文' };

const emptyForm = () => ({
  id: null,
  category: 'announcement',
  date: new Date().toISOString().slice(0, 10),
  title: { ja: '' },
  excerpt: { ja: '' },
  content: { ja: '' },
});

const NewsTab = ({ authHeaders }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  const headers = () => (typeof authHeaders === 'function' ? authHeaders() : authHeaders);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/news`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('お知らせの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openNew = () => { setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (item) => {
    setForm({
      id: item.id,
      category: item.category || 'announcement',
      date: (item.date || '').slice(0, 10),
      title: item.title || { ja: '' },
      excerpt: item.excerpt || { ja: '' },
      content: item.content || { ja: '' },
    });
    setDialogOpen(true);
  };

  const setJa = (field, value) => setForm((f) => ({ ...f, [field]: { ...f[field], ja: value } }));

  const handleTranslate = async () => {
    if (!form.title.ja?.trim()) { toast.error('先に日本語のタイトルを入力してください'); return; }
    if (!window.confirm('日本語の内容を他の7言語へ自動翻訳します。既存の翻訳は上書きされます。よろしいですか？')) return;
    try {
      setTranslating(true);
      const res = await axios.post(`${API}/admin/translate`, {
        texts: { title: form.title.ja || '', excerpt: form.excerpt.ja || '', content: form.content.ja || '' },
        targetLangs: TARGET_LANGS,
        sourceLang: 'ja',
      }, { headers: headers() });
      const tr = res.data?.translations || {};
      setForm((f) => ({
        ...f,
        title: { ...f.title, ...(tr.title || {}) },
        excerpt: { ...f.excerpt, ...(tr.excerpt || {}) },
        content: { ...f.content, ...(tr.content || {}) },
      }));
      toast.success('自動翻訳が完了しました（保存を忘れずに）');
    } catch (e) {
      toast.error(e.response?.data?.detail || '翻訳に失敗しました');
    } finally {
      setTranslating(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.ja?.trim()) { toast.error('タイトル（日本語）は必須です'); return; }
    const payload = {
      title: form.title, excerpt: form.excerpt, content: form.content,
      date: form.date, category: form.category,
    };
    try {
      setSaving(true);
      if (form.id) {
        await axios.put(`${API}/admin/news/${form.id}`, payload, { headers: headers() });
        toast.success('お知らせを更新しました');
      } else {
        await axios.post(`${API}/admin/news`, payload, { headers: headers() });
        toast.success('お知らせを公開しました');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`「${item.title?.ja || ''}」を削除しますか？この操作は取り消せません。`)) return;
    try {
      await axios.delete(`${API}/admin/news/${item.id}`, { headers: headers() });
      toast.success('削除しました');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || '削除に失敗しました');
    }
  };

  const translatedCount = (field) => Object.keys(form[field] || {}).filter((k) => (form[field][k] || '').trim()).length;

  return (
    <TabsContent value="news" className="p-0">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2"><Newspaper className="h-4 w-4" /> お知らせ管理</h3>
          <Button onClick={openNew} size="sm" className="bg-[#00A651] hover:bg-[#008c44]" data-testid="news-add-btn">
            <Plus className="h-4 w-4 mr-1" /> 新規お知らせ
          </Button>
        </div>

        {loading ? (
          <p className="text-gray-500 text-sm py-8 text-center">読み込み中...</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border border-dashed rounded-lg" data-testid="news-admin-empty">
            <Newspaper className="h-10 w-10 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">まだお知らせがありません。「新規お知らせ」から公開情報を追加できます。</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="news-admin-list">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-3 border rounded-lg p-3 bg-white hover:border-[#00A651] transition-colors" data-testid={`news-row-${item.id}`}>
                <Badge className={item.category === 'skill' ? 'bg-[#00A651] text-white' : 'bg-gray-200 text-gray-700'}>
                  {item.category === 'skill' ? '技能講習' : 'お知らせ'}
                </Badge>
                <div className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                  <Calendar className="h-3 w-3" />{(item.date || '').slice(0, 10)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{item.title?.ja || '(無題)'}</p>
                  <p className="text-xs text-gray-500 truncate">{item.excerpt?.ja || ''}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => openEdit(item)} data-testid={`news-edit-${item.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(item)} data-testid={`news-delete-${item.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="news-dialog">
          <DialogHeader>
            <DialogTitle>{form.id ? 'お知らせを編集' : '新規お知らせ'}</DialogTitle>
            <DialogDescription>日本語で入力し、「他言語へ自動翻訳」で多言語化できます。未翻訳の言語は自動的に日本語で表示されます。</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>カテゴリー</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="news-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="announcement">お知らせ</SelectItem>
                    <SelectItem value="skill">技能講習</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>公開日</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="news-date-input" />
              </div>
            </div>

            <div>
              <Label>タイトル（日本語）<span className="text-red-500">*</span></Label>
              <Input value={form.title.ja || ''} onChange={(e) => setJa('title', e.target.value)} placeholder="例：2026年3月度運転技能講習の開講予定" data-testid="news-title-input" />
            </div>

            <div>
              <Label>概要（一覧に表示・日本語）</Label>
              <Textarea rows={2} value={form.excerpt.ja || ''} onChange={(e) => setJa('excerpt', e.target.value)} placeholder="一覧ページに表示される短い説明" data-testid="news-excerpt-input" />
            </div>

            <div>
              <Label>本文（詳細ページに表示・日本語）</Label>
              <Textarea rows={8} value={form.content.ja || ''} onChange={(e) => setJa('content', e.target.value)} placeholder="詳細ページに表示される本文" data-testid="news-content-input" />
            </div>

            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-600">
                翻訳済み言語: タイトル {translatedCount('title')}/8 ・ 本文 {translatedCount('content')}/8
              </div>
              <Button type="button" variant="outline" size="sm" onClick={handleTranslate} disabled={translating} data-testid="news-translate-btn">
                <Languages className="h-4 w-4 mr-1" /> {translating ? '翻訳中...' : '他言語へ自動翻訳'}
              </Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {['ja', ...TARGET_LANGS].map((lg) => (
                <Badge key={lg} variant="outline" className={(form.title[lg] || '').trim() ? 'border-[#00A651] text-[#00A651]' : 'text-gray-400'}>
                  {LANG_LABELS[lg]}
                </Badge>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#00A651] hover:bg-[#008c44]" data-testid="news-save-btn">
              {saving ? '保存中...' : (form.id ? '更新する' : '公開する')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
};

export default NewsTab;
